import React, { createContext, useContext, useMemo, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';

export type TrendChannel = 'all' | 'dvh' | 'foodservice';

export const TREND_CHANNELS: TrendChannel[] = ['all', 'dvh', 'foodservice'];

export const TREND_CHANNEL_LABELS: Record<TrendChannel, string> = {
  all: 'Alla kanaler',
  dvh: 'DVH',
  foodservice: 'Foodservice',
};

/**
 * Objects across the reader views (articles, opportunities, trend cards) all
 * expose dvh/foodservice angles, and trend cards additionally carry numeric
 * potential scores. This shape lets a single matcher work for all of them.
 */
export type TrendChannelSource = {
  dvhAngle?: string;
  foodserviceAngle?: string;
  fontanaAnalysis?: { dvhAngle?: string; foodserviceAngle?: string };
  scores?: { dvhPotential?: number; foodservicePotential?: number };
};

const NEGATIVE_ANGLE = /^(saknas|ingen|inga|inget|ej\b|inte\b|n\/a|-|–|—|okänd|oklar|unknown|none)/i;

function hasMeaningfulAngle(value?: string): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  return trimmed.length >= 8 && !NEGATIVE_ANGLE.test(trimmed);
}

const POTENTIAL_THRESHOLD = 45;

export function matchesTrendChannel(item: TrendChannelSource | null | undefined, channel: TrendChannel): boolean {
  if (channel === 'all') return true;
  if (!item) return false;
  if (channel === 'dvh') {
    return hasMeaningfulAngle(item.dvhAngle)
      || hasMeaningfulAngle(item.fontanaAnalysis?.dvhAngle)
      || (typeof item.scores?.dvhPotential === 'number' && item.scores.dvhPotential >= POTENTIAL_THRESHOLD);
  }
  return hasMeaningfulAngle(item.foodserviceAngle)
    || hasMeaningfulAngle(item.fontanaAnalysis?.foodserviceAngle)
    || (typeof item.scores?.foodservicePotential === 'number' && item.scores.foodservicePotential >= POTENTIAL_THRESHOLD);
}

type TrendChannelContextValue = {
  channel: TrendChannel;
  setChannel: (channel: TrendChannel) => void;
};

const TrendChannelContext = createContext<TrendChannelContextValue>({
  channel: 'all',
  setChannel: () => undefined,
});

export function TrendChannelProvider({ children }: { children: React.ReactNode }) {
  const [channel, setChannel] = useState<TrendChannel>('all');
  const value = useMemo(() => ({ channel, setChannel }), [channel]);
  return <TrendChannelContext.Provider value={value}>{children}</TrendChannelContext.Provider>;
}

export function useTrendChannel(): TrendChannelContextValue {
  return useContext(TrendChannelContext);
}

/**
 * Compact channel selector intended to live in a page filter row next to the
 * search/run filters, rather than in the module header.
 */
export function TrendChannelFilter({ sx, minWidth = 170 }: { sx?: object; minWidth?: number }) {
  const { channel, setChannel } = useTrendChannel();
  return (
    <TextField
      select
      size="small"
      label="Kanal"
      value={channel}
      onChange={(event) => setChannel(event.target.value as TrendChannel)}
      sx={{ minWidth, ...sx }}
    >
      {TREND_CHANNELS.map((item) => (
        <MenuItem key={item} value={item}>
          {TREND_CHANNEL_LABELS[item]}
        </MenuItem>
      ))}
    </TextField>
  );
}
